import React, { useCallback, useEffect, useRef, useState } from "react";
import FoodItem from "./FoodItem";
import Skeleton from "./Skeleton";
import AutoScrollCategories from "./AutoScrollCategories";
import SubcategoryList from "./SubcategoryList";
import { useDispatch, useSelector } from "react-redux";
import { fetchProducts, setOffersMode, fetchOfferProducts } from "../../redux/menu/ProductSlice";
import LocationButton from "../Header/LocationButton";
import { useNavigate } from "react-router-dom";
import { FaHeart } from "react-icons/fa";
import { FaTimes, FaWhatsapp, FaBars } from "react-icons/fa";
import useTheme from "../../hooks/useTheme";

// Add mobile-specific styles
const mobileStyles = `
  @media (max-width: 640px) {
    .category-division {
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
      -ms-overflow-style: none;
      padding-left: 8px !important;
      padding-right: 8px !important;
      will-change: transform;
      transform: translateZ(0);
      backface-visibility: hidden;
      perspective: 1000px;
    }
    .category-division::-webkit-scrollbar {
      display: none;
    }
    .sticky-categories {
      top: 64px !important;
      left: 0 !important;
      right: 0 !important;
      width: 100% !important;
      z-index: 50 !important;
    }
  }
  @media (min-width: 641px) and (max-width: 768px) {
    .sticky-categories {
      top: 64px !important;
    }
  }
  @media (min-width: 769px) and (max-width: 1024px) {
    .sticky-categories {
      top: 88px !important;
    }
  }
  @media (min-width: 1025px) {
    .sticky-categories {
      top: 120px !important;
    }
  }
`;

const Foods = ({
  showCartButton = true,
  showAddToCart = true,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  
  // Handle product navigation
  const handleViewDetails = (productId) => {
    // Get store ID from current URL
    const currentPath = window.location.pathname;
    const pathParts = currentPath.split('/').filter(Boolean);
    const storeId = pathParts[0]; // First part should be store ID
    
    console.log('Navigation details:', { currentPath, pathParts, storeId, productId });
    
    if (storeId) {
      const navigationPath = `/${storeId}/product/${productId}`;
      console.log('Navigating to:', navigationPath);
      navigate(navigationPath);
    } else {
      console.error('Store ID not found in URL');
    }
  };
  const menuReduxValue = useSelector((state) => state.menu);
  const { categories, status: categoriesStatus } = useSelector(
    (state) => state.category
  );
  const {
    products,
    status: productsStatus,
    isSearchMode,
    isOffersMode,
    selectedCategoryId,
    searchQuery,
  } = useSelector((state) => state.product);
  const { profile } = useSelector((state) => state.profile);
  const { banners, status: bannerStatus } = useSelector((state) => state.banner);
  // Sticky
  const [isSticky, setIsSticky] = useState(false);
  const stickyRef = useRef(null);
  
  // Check if banner exists to adjust spacing
  const bannerList = React.useMemo(() => banners?.banner || banners || [], [banners]);
  const hasBanner = (bannerStatus === 'succeeded' || bannerStatus === 'failed') && bannerList && bannerList.length > 0;

  const [menuTab, setMenuTab] = useState(() => {
    // Restore from sessionStorage if available
    const saved = sessionStorage.getItem('savedCategory');
    return saved || menuReduxValue?.selectedCategory;
  });
  
  const [currentCategoryId, setCurrentCategoryId] = useState(() => {
    return sessionStorage.getItem('savedCategoryId') || "";
  });
  
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  
  // Subcategory state
  const [selectedSubcategory, setSelectedSubcategory] = useState(() => {
    return sessionStorage.getItem('savedSubcategory') || "All";
  });
  
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState(() => {
    const saved = sessionStorage.getItem('savedSubcategoryId');
    return saved && saved !== 'null' ? saved : null;
  });

  const [whatsappMessage, setWhatsappMessage] = useState("");

  // Load products with saved state on mount if available
  useEffect(() => {
    const savedOffers = sessionStorage.getItem('savedOffers');
    if (savedOffers === 'true') {
      dispatch(setOffersMode(true));
      dispatch(fetchOfferProducts({}));
      setTimeout(() => {
        sessionStorage.removeItem('savedOffers');
      }, 500);
      return; // Skip category restore when returning from offers
    }
    const savedCatId = sessionStorage.getItem('savedCategoryId');
    const savedSubId = sessionStorage.getItem('savedSubcategoryId');
    const savedCategory = sessionStorage.getItem('savedCategory');
    const savedSubcategory = sessionStorage.getItem('savedSubcategory');
    
    if (savedCatId) {
      // Load products with saved category and subcategory
      const subId = savedSubId && savedSubId !== 'null' ? savedSubId : null;
      loadProducts(savedCatId, 1, subId);
      
      // Restore the category and subcategory names
      if (savedCategory) {
        setMenuTab(savedCategory);
      }
      if (savedSubcategory) {
        setSelectedSubcategory(savedSubcategory);
      }
      
      // Clear after loading
      setTimeout(() => {
        sessionStorage.removeItem('savedCategory');
        sessionStorage.removeItem('savedCategoryId');
        sessionStorage.removeItem('savedSubcategory');
        sessionStorage.removeItem('savedSubcategoryId');
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Update whatsapp message when search query changes
  useEffect(() => {
    if (searchQuery) {
      setWhatsappMessage(`Hi, I'm looking for: ${searchQuery}`);
    }
  }, [searchQuery]);

  // Set default message for offers mode
  useEffect(() => {
    if (isOffersMode) {
      setWhatsappMessage("Hi, I'm looking for: offer");
    }
  }, [isOffersMode]);

  // Close modal on Escape key press
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && showCategoryModal) {
        setShowCategoryModal(false);
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showCategoryModal]);

  const loadProducts = useCallback(
    (categoryId, page = 1, subcategoryId = null) => {
      if (categoryId) {
        const params = { 
          categoryID: categoryId, 
          page,
          subCategoryId: subcategoryId // Pass subcategoryId (will be null for "All")
        };
        dispatch(fetchProducts(params));
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (categories && categories.length > 0) {
      if (!menuTab && categories[0]) {
        setMenuTab(categories[0].name);
        setCurrentCategoryId(categories[0].id);
        loadProducts(categories[0].id, 1);
      }
    }
  }, [categories, menuTab, dispatch, loadProducts]);

  useEffect(() => {
    if (menuTab && categories) {
      const selectedCategory = categories.find((cat) => cat.name === menuTab);
      if (selectedCategory && selectedCategory.id !== currentCategoryId) {
        setCurrentCategoryId(selectedCategory.id);
        // Load products with saved subcategory if available
        // fetchProducts will set status to "loading" automatically, no need to clear first
        loadProducts(selectedCategory.id, 1, selectedSubcategoryId);
      }
    }
  }, [menuTab, categories, currentCategoryId, loadProducts, selectedSubcategoryId]);

  // Sync with Redux selectedCategoryId (when category is selected from search results)
  useEffect(() => {
    if (selectedCategoryId && categories) {
      const category = categories.find((cat) => cat.id === selectedCategoryId);
      if (category && category.name !== menuTab) {
        setMenuTab(category.name);
        setCurrentCategoryId(selectedCategoryId);
      }
    }
  }, [selectedCategoryId, categories, menuTab]);

  // Cart persistence is handled by localStorage, no need to reset on mount

  const handleMenuTabs = useCallback((type, index) => {
    // Don't re-select if the same category is already selected
    if (menuTab === type) {
      return;
    }
    setMenuTab(type);
    setSelectedSubcategory("All"); // Reset subcategory when changing main category
    setSelectedSubcategoryId(null); // Reset subcategory ID
    // Products will be loaded automatically via useEffect that watches menuTab
  }, [menuTab]);

  // Get subcategories for the currently selected category
  const currentSubcategories = React.useMemo(() => {
    const selectedCategory = categories?.find((cat) => cat.name === menuTab);
    return selectedCategory?.ProductSubCategories || [];
  }, [categories, menuTab]);

  // Handle subcategory selection
  const handleSubcategorySelect = (subcategoryName) => {
    setSelectedSubcategory(subcategoryName);
  };

  // Handle subcategory ID selection and reload products
  const handleSubcategoryIdSelect = (subcategoryId) => {
    setSelectedSubcategoryId(subcategoryId);
    
    // Reload products with subcategoryId if it's not "All"
    if (currentCategoryId) {
      loadProducts(currentCategoryId, 1, subcategoryId);
    }
  };

  // Save current state before navigating to product details
  const saveCurrentState = () => {
    sessionStorage.setItem('savedCategory', menuTab);
    sessionStorage.setItem('savedCategoryId', currentCategoryId);
    sessionStorage.setItem('savedSubcategory', selectedSubcategory);
    sessionStorage.setItem('savedSubcategoryId', selectedSubcategoryId || 'null');
    sessionStorage.setItem('savedOffers', isOffersMode ? 'true' : 'false');
  };

  // No need to filter products - API already returns filtered results based on subCategoryId
  const filteredProducts = React.useMemo(() => {
    return products || [];
  }, [products]);

  // Sticky handler like plain JS
  useEffect(() => {
    const handleScroll = () => {
      if (!stickyRef.current) return;
      const stickyTop = stickyRef.current.offsetTop;
      setIsSticky(window.pageYOffset > stickyTop);
    };

    window.addEventListener("scroll", handleScroll);
    // Also check on products/categories change to recalculate position
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [products, categories]);

  // Calculate total favorites items
  const favorites = useSelector((state) => state.favorites.favorites);
  const totalFavorites = favorites ? favorites.length : 0;

  // Cart button refs and handlers
  const iconRef = useRef(null);
  const handleCartMouseEnter = () => {
    if (iconRef.current) {
      iconRef.current.style.transform = "scale(1.1) rotate(5deg)";
    }
  };
  const handleCartMouseLeave = () => {
    if (iconRef.current) {
      iconRef.current.style.transform = "scale(1) rotate(0deg)";
    }
  };

  return (
    <>
      <style>{mobileStyles}</style>
      
      {/* Show location button when no banner (regardless of products) */}
      {!hasBanner && !isSearchMode && !isOffersMode && (
        <div className="max-w-screen-xl mx-auto px-4 md:px-6 pt-16 md:pt-28 pb-0">
          <div className="md:ml-2 md:pt-2">
            <LocationButton />
          </div>
        </div>
      )}
      
      <section
        className={`${
          isSticky ? "pt-4 md:pt-6" : ""
        } ${
          !hasBanner && categories && categories.length > 0 && !isSearchMode && !isOffersMode
            ? "pt-4 md:pt-8" 
            : !hasBanner && isOffersMode && products && products.length === 0
            ? "pt-20 md:pt-28" 
            : !hasBanner && isOffersMode && products && products.length > 0
            ? "pt-20 md:pt-32"
            : !hasBanner && isOffersMode
            ? "pt-20 md:pt-32"
            : !hasBanner && isSearchMode && products && products.length === 0
            ? "pt-0 md:pt-0"
            : !hasBanner && isSearchMode
            ? "pt-20 md:pt-32"
            : !hasBanner 
            ? "pt-0 md:pt-0" 
            : "md:my-12"
        } max-w-screen-xl mx-auto px-4 md:px-6 pb-8 md:pb-12`}
      >
        {/* Sticky Header - Only show when not in search mode and not in offers mode */}
        {!isSearchMode && !isOffersMode && (
          <div
            ref={stickyRef}
            className={`${
              isSticky
                ? "fixed left-0 right-0 z-50 bg-white shadow-md sticky-categories"
                : ""
            }`}
          >
            <AutoScrollCategories
              categories={categories}
              categoriesStatus={categoriesStatus}
              selectedCategory={menuTab}
              onCategorySelect={handleMenuTabs}
              onFilterClick={() => setShowCategoryModal(true)}
              isSticky={isSticky}
            />
            
            {/* Subcategory List - Only show if subcategories exist */}
            {currentSubcategories.length > 0 && (
              <SubcategoryList
                subcategories={currentSubcategories}
                selectedSubcategory={selectedSubcategory}
                onSubcategorySelect={handleSubcategorySelect}
                onSubcategoryIdSelect={handleSubcategoryIdSelect}
              />
            )}
          </div>
        )}

        {/* Subcategory List - Show after categories if subcategories exist */}
        {!isSearchMode && !isOffersMode && currentSubcategories.length > 0 && (
          <div className={isSticky ? "mt-16 md:mt-20" : ""}>
            <SubcategoryList
              subcategories={currentSubcategories}
              selectedSubcategory={selectedSubcategory}
              onSubcategorySelect={handleSubcategorySelect}
              onSubcategoryIdSelect={handleSubcategoryIdSelect}
            />
          </div>
        )}

        {/* Offers Results Header - Only show when offers mode is active */}
        {/* {isOffersMode &&
          productsStatus === "succeeded" &&
          filteredProducts &&
          filteredProducts.length > 0 && (
            <div className="text-center mt-4 mb-2"> */}

        {/* Category filter button for offers mode with no products */}
        {isOffersMode && products && products.length === 0 && (
          <div className={`flex justify-center mb-2 ${!hasBanner ? "mt-32 md:mt32" : "mt-4"}`}>
            <button
              onClick={() => setShowCategoryModal(true)}
              className="cursor-pointer hover:scale-110 transition-transform"
              title="View All Categories"
            >
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center shadow-md hover:shadow-lg transition-shadow"
                style={{
                  border: `1px solid ${theme.colors.allCategoriesBorder}`,
                  backgroundColor: theme.colors.allCategoriesBg || "#ffffff",
                }}
              >
                <FaBars
                  className="w-6 h-6"
                  style={{ color: theme.colors.menuIconColor || theme.colors.primaryColor }}
                />
              </div>
            </button>
          </div>
        )}

        {/* Category filter button for search mode with products found */}
        {isSearchMode && products && products.length > 0 && (
          <div className="flex justify-center mb-4">
            <button
              onClick={() => setShowCategoryModal(true)}
              className="cursor-pointer hover:scale-110 transition-transform"
              title="View All Categories"
            >
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center shadow-md hover:shadow-lg transition-shadow"
                style={{
                  border: `1px solid ${theme.colors.allCategoriesBorder}`,
                  backgroundColor: theme.colors.allCategoriesBg || "#ffffff",
                }}
              >
                <FaBars
                  className="w-6 h-6"
                  style={{ color: theme.colors.menuIconColor || theme.colors.primaryColor }}
                />
              </div>
            </button>
          </div>
        )}

        {/* Category filter button for offers mode with products found */}
        {isOffersMode && products && products.length > 0 && (
          <div className="flex justify-center mb-4">
            <button
              onClick={() => setShowCategoryModal(true)}
              className="cursor-pointer hover:scale-110 transition-transform"
              title="View All Categories"
            >
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center shadow-md hover:shadow-lg transition-shadow"
                style={{
                  border: `1px solid ${theme.colors.allCategoriesBorder}`,
                  backgroundColor: theme.colors.allCategoriesBg || "#ffffff",
                }}
              >
                <FaBars
                  className="w-6 h-6"
                  style={{ color: theme.colors.menuIconColor || theme.colors.primaryColor }}
                />
              </div>
            </button>
          </div>
        )}

        {/* Products Grid */}
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 lg:gap-6 ${
            !isSearchMode ? "mt-6 md:mt-10" : "-mt-2"
          } h-[420px] px-1 md:px-0 mb-8 md:mb-12`}
        >
          {productsStatus === "loading" && filteredProducts.length === 0 ? (
            Array.from({ length: 8 }).map((_, index) => (
              <Skeleton key={index} />
            ))
          ) : products.length === 0 ? (
            <div className="col-span-1 sm:col-span-2 flex items-center justify-center py-4 h-[420px]">
              <div className="bg-white/70 backdrop-blur-md rounded-3xl shadow-2xl px-8 py-10 flex flex-col items-center max-w-md w-full border border-gray-100">
                  <svg
                    className="w-20 h-20 mb-6 text-primary/80"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      cx="11"
                      cy="11"
                      r="8"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      fill="#f3f4f6"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 21l-4.35-4.35"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M9.5 10.5c.5-1 2.5-1 3 0"
                      stroke="#f91944"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                    />
                    <circle cx="9" cy="9" r="1" fill="#f91944" />
                    <circle cx="13" cy="9" r="1" fill="#f91944" />
                  </svg>
                  <h2 className="text-2xl font-extrabold text-primary mb-2 poppins text-center">
                    {isOffersMode ? "No offers found" : "No items found"}
                  </h2>
                  <p className="text-gray-500 text-base text-center mt-2">
                    {isSearchMode 
                      ? "We couldn't find what you're looking for." 
                      : isOffersMode
                      ? "We couldn't find any offers at the moment."
                      : "We couldn't find any menu items in this category."}
                  </p>
                  
                  {/* WhatsApp Contact - Show in both search mode and empty categories */}
                  {profile?.whatsapp && (
                    <div className="w-full mt-6 space-y-3">
                      <p className="text-gray-700 text-sm text-center font-medium">
                        {isSearchMode ? "Can't find it? Contact us on WhatsApp!" : "Looking for something? Contact us on WhatsApp!"}
                      </p>
                      <textarea
                        value={whatsappMessage}
                        onChange={(e) => setWhatsappMessage(e.target.value)}
                        placeholder={isSearchMode ? "Enter what you're looking for..." : isOffersMode ? "Hi, I'm looking for: offer" : "Tell us what you need..."}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl resize-none focus:outline-none focus:border-primary transition-colors text-sm"
                        rows="3"
                      />
                      <button
                        onClick={() => {
                          if (whatsappMessage.trim()) {
                            // Construct professional message
                            let message = isSearchMode ? "🔍 *PRODUCT INQUIRY*\n\n" : "📦 *CATEGORY INQUIRY*\n\n";
                            message += "📋 *INQUIRY DETAILS*\n";
                            message += "━━━━━━━━━━━━━━━━━━━━\n";
                            message += `${isSearchMode ? 'Looking for' : 'Message'}: *${whatsappMessage}*\n`;
                            message += isSearchMode ? "Status: Item not found in online menu\n\n" : "Status: Category has no items\n\n";
                            
                            message += "❓ *QUESTIONS*\n";
                            message += "━━━━━━━━━━━━━━━━━━━━\n";
                            message += "• Is this item available?\n";
                            message += "• What is the price?\n";
                            message += "• Can I place an order?\n\n";
                            
                            message += "🏪 *STORE INFORMATION*\n";
                            message += "━━━━━━━━━━━━━━━━━━━━\n";
                            message += `Store Name: ${profile.name || 'N/A'}\n`;
                            if (profile.location) {
                              message += `Location: ${profile.location}\n`;
                          }
                          if (profile.mobile) {
                              message += `Phone: ${profile.mobile}\n`;
                          }
                          if (profile.link) {
                              message += `Website: ${profile.link}\n`;
                          }
                          message += "\n";
                          
                          message += "⏰ *INQUIRY TIME*\n";
                          message += "━━━━━━━━━━━━━━━━━━━━\n";
                          message += `Date: ${new Date().toLocaleDateString()}\n`;
                          message += `Time: ${new Date().toLocaleTimeString()}\n\n`;
                          
                          message += "Thank you for your assistance! 🙏";
                          
                          const url = `https://wa.me/${profile.whatsapp}?text=${encodeURIComponent(message)}`;
                          window.open(url, '_blank');
                        }
                      }}
                      className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-6 rounded-xl flex items-center justify-center gap-2 transition-all duration-200 shadow-md hover:shadow-lg"
                    >
                      <FaWhatsapp className="w-5 h-5" />
                      Connect on WhatsApp
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            (() => {
              // Process filtered products to swap highlighted items in even positions
              const processedProducts = [...filteredProducts];

              for (let i = 0; i < processedProducts.length; i++) {
                const currentItem = processedProducts[i];
                const position = i + 1; // index + 1 for position

                // Check if current item is highlighted and in even position
                if (currentItem?.highlight && position % 2 === 0 && i > 0) {
                  const previousItem = processedProducts[i - 1];

                  // Check if previous item is not highlighted
                  if (!previousItem?.highlight) {
                    // Swap positions
                    processedProducts[i] = previousItem;
                    processedProducts[i - 1] = currentItem;
                  }
                }
              }

              return processedProducts.map((item) => {
                const currentCategory = categories?.find(
                  (cat) => cat.name === menuTab
                );
                return (
                  <FoodItem
                    key={item.id}
                    {...item}
                    currency={profile?.currency || "AED"}
                    image={item?.DocImage?.imageUrl}
                    is_veg={item?.is_veg}
                    categoryName={currentCategory?.name}
                    categoryDescription={currentCategory?.description}
                    subcategoryName={selectedSubcategory}
                    currentCategoryId={currentCategoryId}
                    selectedSubcategoryId={selectedSubcategoryId}
                    showAddToCart={showAddToCart}
                    onViewDetails={handleViewDetails}
                    highlight={item?.highlight || false}
                    profile={profile}
                  />
                );
              });
            })()
          )}
        </div>

        {/* Cart Button with Count - Hidden on Mobile */}
        {showCartButton && (
          <div className="fixed z-50 bottom-6 right-6 hidden md:block">
            <button
              className="rounded-full shadow-2xl w-20 h-20 flex items-center justify-center text-4xl border-2 border-opacity-90 transition-transform duration-300 hover:scale-110 relative"
              style={{
                background: `linear-gradient(to bottom right, ${theme.colors.cartIconGradientFrom}, ${theme.colors.cartIconGradientVia}, ${theme.colors.cartIconGradientTo})`,
                color: theme.colors.cartIconText,
                borderColor: theme.colors.cartIconBorder,
                borderWidth: "2.5px",
                boxShadow: theme.colors.cartIconShadow,
              }}
              aria-label="View Favorites"
              onClick={() => navigate(`/${profile?.key}/orders`)}
              onMouseEnter={handleCartMouseEnter}
              onMouseLeave={handleCartMouseLeave}
            >
              <span ref={iconRef} className="inline-block text-5xl font-bold">
                <FaHeart />
              </span>
              {totalFavorites > 0 && (
                <div
                  className="text-xs font-bold rounded-full w-7 h-7 flex items-center justify-center z-10 border-2 shadow-lg absolute -top-1 -right-1"
                  style={{
                    backgroundColor: theme.colors.cartIconBadgeBg,
                    color: theme.colors.cartIconBadgeText,
                    borderColor: theme.colors.cartIconBorder,
                  }}
                >
                  {totalFavorites}
                </div>
              )}
            </button>
          </div>
        )}

        {/* Category Selection Modal */}
        {showCategoryModal && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50 p-4 pb-32 md:pb-12"
            onClick={() => setShowCategoryModal(false)}
          >
            <div
              className="bg-white rounded-t-xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col"
              style={{ backgroundColor: theme.colors.backgroundColor }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div
                className="flex items-center justify-between p-4 border-b"
                style={{ borderColor: theme.colors.borderColor }}
              >
                <button
                  onClick={() => setShowCategoryModal(false)}
                  className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
                  style={{ color: theme.colors.textSecondary }}
                >
                  <FaTimes className="w-5 h-5" />
                </button>
                <h3
                  className="text-lg font-semibold"
                  style={{ color: theme.colors.textPrimary }}
                >
                  Menu categories
                </h3>
                <div className="w-8"></div> {/* Spacer for centering */}
              </div>

              {/* Modal Content - Vertical List */}
              <div
                className="overflow-y-auto flex-1"
                style={{
                  maxHeight: "60vh",
                  minHeight: "200px",
                  WebkitOverflowScrolling: "touch",
                  scrollbarWidth: "thin",
                  scrollbarColor: `${theme.colors.borderColor} transparent`,
                }}
              >
                {categories &&
                  categories.map((categoryItem, index) => (
                    <button
                      key={categoryItem.id || index}
                      onClick={() => {
                        handleMenuTabs(categoryItem.name, index);
                        setShowCategoryModal(false);
                      }}
                      className="w-full flex items-center justify-between py-4 px-4 hover:bg-gray-50 transition-colors border-l-4 min-h-[60px]"
                      style={{
                        borderLeftColor:
                          menuTab === categoryItem.name
                            ? theme.colors.primaryColor
                            : "transparent",
                        backgroundColor:
                          menuTab === categoryItem.name
                            ? theme.colors.primaryColor + "05"
                            : "transparent",
                      }}
                    >
                      <div className="flex items-center space-x-3">
                        {categoryItem.DocImage?.imageUrl ? (
                          <img
                            src={categoryItem.DocImage.imageUrl}
                            alt={categoryItem.name}
                            className="w-6 h-6 object-contain rounded-full"
                          />
                        ) : (
                          <div className="w-6 h-6 rounded-full flex items-center justify-center">
                            <span className="text-lg">🍽️</span>
                          </div>
                        )}
                        <span
                          className="font-medium"
                          style={{
                            color: theme.colors.textPrimary,
                          }}
                        >
                          {categoryItem.name}
                        </span>
                      </div>
                    </button>
                  ))}
              </div>
            </div>
          </div>
        )}
      </section>
    </>
  );
};

export default Foods;
